import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Waves, Bed, Users, AlertCircle, ExternalLink } from "lucide-react";

const PAYMENT_LINKS = {
  venmo:   "https://account.venmo.com/u/rachel-long-6",
  paypal:  "https://www.paypal.com/paypalme/rachelbelongs",
  revolut: "https://revolut.me/rachellong87",
};

const roomImages = {
  "Premium Oceanview":  "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/6ecm6ecz_Comfort-Oceaview-Premium-2019-4-1024x680-9888000-6926041.webp",
  "Superior Oceanside": "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/9zk9qxmn_Comfort-Oceanside-Room-2019-2-1024x680-9888000-6925854.webp",
  "Oceanside Room":     "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/omah9yz9_Ocean-Side-Room-9375373.webp",
  "Superior Room":      "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/o2htbz88_SuperiorRoom-100-4928081.webp",
  "Standard Room":      "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/sh1ur8kv_standard-room-new-1-9888007-9373428.webp",
};

export default function Stay() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ guest_name: "", partner_name: "", roommates: ["", ""], email: "", phone: "" });

  const load = async () => {
    try {
      const res = await apiClient.get("/rooms");
      setSummary(res.data);
    } catch {
      toast.error("Couldn't load rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isPlaceholderName = (s) => {
    const v = (s || "").trim().toLowerCase();
    if (v.length < 3) return true;
    const blocked = ["tbd", "tba", "n/a", "na", "idk", "unknown", "?", "??", "???", "x", "xx", "none", "tbc"];
    return blocked.includes(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.guest_name || !form.email) {
      toast.error("Name and email required.");
      return;
    }
    if (form.partner_name.trim() && isPlaceholderName(form.partner_name)) {
      toast.error("Please add a real name (no TBDs) or leave it blank.");
      return;
    }
    const max = claim?.max_occupancy || 2;
    const slots = Math.max(0, max - 2); // beyond guest + partner
    const roommates = (form.roommates || []).slice(0, slots).map((r) => (r || "").trim());
    if (roommates.some((r) => r && isPlaceholderName(r))) {
      toast.error("Please add real names for roommates (no TBDs) or leave them blank.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/rooms/claim-type/${encodeURIComponent(claim.room_type)}`, {
        guest_name: form.guest_name,
        partner_name: form.partner_name,
        email: form.email,
        phone: form.phone,
        roommates: roommates.filter((r) => r),
        extras: roommates.filter((r) => r).length,
      });
      toast.success("Got it — room held while I confirm your payment.");
      setClaim(null);
      setForm({ guest_name: "", partner_name: "", roommates: ["", ""], email: "", phone: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream">
      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden" data-testid="stay-hero">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
          <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-4">Stay at</p>
          <h1 className="font-display text-6xl md:text-8xl font-black text-ink leading-[0.9]">
            Villa <span className="italic text-sage-dark">Taina</span>
          </h1>
          <p className="text-lg text-muted_ink mt-5 max-w-2xl">
            Oceanfront. 38 rooms. Three nights, May 13–16. Rates include breakfast and taxes. Your room isn't official until payment lands, so fill out the form and send your deposit in one go.
          </p>
          <a
            href="https://villataina.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-terracotta font-medium mt-4 hover:gap-2.5 transition-all"
            data-testid="villa-taina-link"
          >
            Visit villataina.com
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* Callouts */}
      <section className="bg-sand border-y border-shell py-6">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-muted_ink">
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-terracotta" /> First come, first serve</span>
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-terracotta" /> No kids at Villa Taina</span>
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-terracotta" /> Rates include breakfast & taxes</span>
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-terracotta" /> Payment required to confirm reservation</span>
        </div>
      </section>

      {/* Amenities */}
      <section className="bg-white py-4 border-b border-shell">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center text-xs tracking-[0.3em] uppercase text-sage-dark font-bold" data-testid="stay-amenities">
          Every room includes a private balcony · free WiFi
        </div>
      </section>

      {/* Fill-the-rooms ask */}
      <section className="bg-gradient-to-r from-gold/20 via-sand to-gold/20 border-b border-shell py-5" data-testid="fill-rooms-note">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="font-display italic text-ink text-base md:text-lg">
            A small ask: please try to fill these rooms to their full <span className="text-terracotta font-bold not-italic">Sleeps</span> count. Buddy up, my loves.
          </p>
        </div>
      </section>

      {/* Rooms grid */}
      <section className="py-16 md:py-20" data-testid="rooms-list">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          {loading ? (
            <p className="text-center text-muted_ink">Loading rooms…</p>
          ) : (
            <div className="space-y-8">
              {summary.map((t, i) => {
                const remaining = t.open;
                const soldOut = remaining === 0;
                return (
                  <article
                    key={t.room_type}
                    className="grid md:grid-cols-5 gap-0 bg-sand border border-shell rounded-lg overflow-hidden"
                    data-testid={`room-type-${i}`}
                  >
                    <div className="md:col-span-2 relative min-h-[240px] md:min-h-[320px] overflow-hidden">
                      <img src={roomImages[t.room_type]} alt={t.room_type} className="w-full h-full object-cover vintage-filter" />
                      <div className="absolute top-4 left-4 bg-cream/95 backdrop-blur rounded-full px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-ink">
                        {t.total} rooms · {t.bed_config}
                      </div>
                    </div>

                    <div className="md:col-span-3 p-6 md:p-10 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div>
                            <h3 className="font-display text-3xl md:text-4xl font-black text-ink leading-tight">
                              {t.room_type}
                            </h3>
                            {t.notes && <p className="text-sm text-muted_ink mt-1 italic">{t.notes}</p>}
                          </div>
                          <div className="text-right">
                            <p className="font-display text-4xl font-black text-terracotta leading-none">
                              ${t.three_night_total}
                            </p>
                            <p className="text-xs uppercase tracking-widest text-muted_ink mt-1">3-night total</p>
                            <p className="text-[10px] uppercase tracking-widest text-sage-dark mt-0.5 font-bold">incl. breakfast & tax</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-6 text-sm text-ink">
                          <span className="flex items-center gap-1.5"><Bed className="w-4 h-4 text-sage-dark" /> {t.bed_config}</span>
                          <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-sage-dark" /> Sleeps {t.max_occupancy}</span>
                          <span className="flex items-center gap-1.5"><Waves className="w-4 h-4 text-sage-dark" /> Villa Taina</span>
                        </div>
                      </div>

                      <div className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-shell flex-wrap">
                        <div>
                          <p className={`font-display text-2xl font-black leading-none ${soldOut ? "text-muted_ink" : "text-sage-dark"}`} data-testid={`room-remaining-${i}`}>
                            {soldOut ? "All claimed" : `${remaining} left`}
                          </p>
                          <p className="text-xs tracking-widest uppercase text-muted_ink mt-1">
                            {t.pending > 0 && `${t.pending} pending · `}{t.confirmed > 0 && `${t.confirmed} confirmed`}
                          </p>
                        </div>
                        <button
                          disabled={soldOut}
                          onClick={() => setClaim({ room_type: t.room_type, three_night_total: t.three_night_total, max_occupancy: t.max_occupancy })}
                          className={`${soldOut ? "bg-shell text-muted_ink cursor-not-allowed rounded-full px-7 py-3 font-medium" : "btn-primary"}`}
                          data-testid={`claim-${t.room_type.toLowerCase().replace(/\s+/g, "-")}-btn`}
                        >
                          {soldOut ? "Sold out" : "Claim a room"}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* No-pressure closing note */}
      <section className="pb-20 md:pb-28" data-testid="stay-closing-note">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="bg-sand border border-shell rounded-lg p-8 md:p-10 text-center">
            <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-4">No pressure</p>
            <h3 className="font-display text-3xl md:text-4xl font-black text-ink leading-tight mb-4">
              Don't panic if you can't <span className="italic text-sage-dark">commit yet.</span>
            </h3>
            <p className="text-muted_ink text-base md:text-lg leading-relaxed">
              If these rooms go quick, Cabarete has plenty of great places to stay just steps from Villa Taina.
              Take a peek at the <Link to="/hotels" className="text-terracotta font-bold underline-offset-4 hover:underline">other hotels page</Link> for vetted spots within a 2–3 minute walk.
            </p>
          </div>
        </div>
      </section>

      {/* Claim dialog */}
      <Dialog open={!!claim} onOpenChange={(o) => !o && setClaim(null)}>
        <DialogContent className="bg-cream border-shell" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-black text-ink">
              Claim · {claim?.room_type}
            </DialogTitle>
            <DialogDescription className="text-muted_ink">
              ${claim?.three_night_total} for 3 nights (breakfast and taxes included). Fill out the form and send your deposit via Venmo, PayPal, or Revolut to lock it in.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Your name *</Label>
                <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} required data-testid="claim-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Roommate</Label>
                <Input
                  value={form.partner_name}
                  onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                  placeholder="Full name (optional)"
                  data-testid="claim-partner"
                />
                <p className="text-[11px] text-muted_ink italic">Buddy up if you can — share their name so I can plan.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="claim-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="claim-phone" />
            </div>

            {claim?.max_occupancy >= 3 && (
              <div className="space-y-3 pt-2">
                <p className="text-xs uppercase tracking-widest text-sage-dark font-bold">
                  {claim.max_occupancy === 4 ? "Additional roommates (optional)" : "3rd guest (optional)"}
                </p>
                {Array.from({ length: claim.max_occupancy - 2 }).map((_, idx) => (
                  <div key={idx} className="space-y-1.5">
                    <Label>
                      {claim.max_occupancy === 4 ? `Roommate ${idx + 1}` : "3rd guest"}
                    </Label>
                    <Input
                      value={form.roommates[idx] || ""}
                      onChange={(e) => {
                        const next = [...form.roommates];
                        next[idx] = e.target.value;
                        setForm({ ...form, roommates: next });
                      }}
                      placeholder="Full name (optional)"
                      data-testid={`claim-roommate-${idx}`}
                    />
                  </div>
                ))}
                {claim.max_occupancy === 4 && (
                  <p className="text-xs text-muted_ink italic">
                    This room sleeps 4 — share names if you've got 'em so I can plan accordingly.
                  </p>
                )}
              </div>
            )}

            <div className="pt-4 mt-2 border-t-2 border-dashed border-shell">
              <p className="font-display text-lg font-black text-ink mb-1">Step 2 · Send your deposit</p>
              <p className="text-xs text-muted_ink mb-4 tracking-wide">
                Total: <span className="text-terracotta font-bold">${claim?.three_night_total}</span> for 3 nights. Note your name + "Rach Fest" in the memo.
              </p>
              <div className="grid grid-cols-3 gap-2" data-testid="payment-links">
                <a href={PAYMENT_LINKS.venmo} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 rounded-xl bg-[#3D95CE] text-white font-bold py-3 hover:-translate-y-0.5 hover:shadow-[0_4px_0_0_#1F5F8B] transition-all"
                   data-testid="pay-venmo-btn">
                  <span className="text-sm">Venmo</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a href={PAYMENT_LINKS.paypal} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 rounded-xl bg-[#003087] text-white font-bold py-3 hover:-translate-y-0.5 hover:shadow-[0_4px_0_0_#001B4E] transition-all"
                   data-testid="pay-paypal-btn">
                  <span className="text-sm">PayPal</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a href={PAYMENT_LINKS.revolut} target="_blank" rel="noreferrer"
                   className="flex flex-col items-center justify-center gap-1 rounded-xl bg-[#0666EB] text-white font-bold py-3 hover:-translate-y-0.5 hover:shadow-[0_4px_0_0_#023C8F] transition-all"
                   data-testid="pay-revolut-btn">
                  <span className="text-sm">Revolut</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full h-12" data-testid="claim-submit">
              {submitting ? "Submitting..." : "I sent payment — hold this room"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
